import { ReactNode, SetStateAction, useCallback, useRef, useState } from "react";

import { StoreObject } from "@apollo/client";
import _ from "lodash";

import { Button, Input, Text, UseDisclosureReturn } from "@chakra-ui/react";

import IconModal from "@/components/shared/IconModal";
import { DeleteIcon } from "@/components/shared/ReleaseIcons";
import legacyErrorHandler from "@/utils/legacyErrorHandler";

type DeleteModalProps = {
  additionalBody?: ReactNode;
  resource?: Omit<StoreObject, "__typename"> & { name?: string | null };
  header?: ReactNode | string;
  deleteCallback: () => void;
  onComplete?: () => void;
  verb?: string;
  confirmWithInput?: boolean;
  confirmationText?: string;
} & UseDisclosureReturn;

function DeleteModal({
  additionalBody,
  resource = {},
  verb = "Delete",
  header,
  onComplete,
  deleteCallback,
  confirmWithInput = true,
  confirmationText,
  ...disclosureProps
}: DeleteModalProps) {
  const { onClose } = disclosureProps;

  const modalHeader = header || (
    <>
      {_.startCase(verb)} {resource.name}
    </>
  );

  const initialRef = useRef<HTMLInputElement>(null);

  const [deleteInput, setDeleteInput] = useState("");
  const handleChange = (event: { target: { value: SetStateAction<string> } }) =>
    setDeleteInput(event.target.value);

  const deleteResource = useCallback(async () => {
    try {
      deleteCallback();
      setDeleteInput("");
    } catch (error) {
      legacyErrorHandler(error);
    }
  }, [deleteCallback]);

  const expectedConfirmationText = confirmationText ?? verb.toUpperCase();

  if (confirmWithInput) {
    return (
      <IconModal
        body={
          <>
            {additionalBody}
            <Text mb="8px">
              Type <strong>{expectedConfirmationText}</strong> to confirm
            </Text>
            <Input
              ref={initialRef}
              data-testid="DeleteModal-input"
              value={deleteInput}
              onChange={handleChange}
              onKeyDown={e => {
                if (e.key === "Enter") {
                  deleteResource();
                }
              }}
            />
          </>
        }
        footer={
          <>
            <Button as="a" mr={3} variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              data-testid="DeleteModal-delete-button"
              isDisabled={deleteInput !== expectedConfirmationText}
              onClick={() => deleteResource()}
            >
              {_.startCase(verb)}
            </Button>
          </>
        }
        header={modalHeader}
        icon={DeleteIcon}
        initialFocusRef={initialRef}
        {...disclosureProps}
      />
    );
  } else {
    return (
      <IconModal
        body={<>{additionalBody}</>}
        footer={
          <>
            <Button as="a" mr={3} variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button data-testid="DeleteModal-delete-button" onClick={() => deleteResource()}>
              {_.startCase(verb)}
            </Button>
          </>
        }
        header={modalHeader}
        icon={DeleteIcon}
        initialFocusRef={initialRef}
        {...disclosureProps}
      />
    );
  }
}

export default DeleteModal;
